<template>
  <div class="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-700">
    
    <div class="max-w-[1600px] mx-auto p-4 md:p-8 lg:p-10">
      <header class="flex  md:flex-row justify-between items-start md:items-center mb-12 gap-6 animate-fade-in-down">
        
        <div class="space-y-2">
          <h1 class="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            Vue d'ensemble
          </h1>
          <div class="flex items-center gap-3 text-slate-500 font-medium">
            <span class="relative flex h-2.5 w-2.5">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
            </span>
            <p>Reporting en temps réel du parc Media Link.</p>
          </div>
        </div>

        <div class="group flex items-center gap-4 bg-white px-6 py-4 rounded-2xl shadow-sm border border-slate-200/60 transition-all duration-300 hover:shadow-xl hover:border-indigo-100 hover:-translate-y-1 cursor-help">
          <div class="relative flex h-4 w-4">
            <span 
              class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              :class="systemHealthClass"
            ></span>
            <span 
              class="relative inline-flex rounded-full h-4 w-4 border-2 border-white shadow-sm"
              :class="systemHealthDotClass"
            ></span>
          </div>
          <div class="flex flex-col">
            <span class="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1 group-hover:text-indigo-500 transition-colors">
              Infrastructure
            </span>
            <span class="text-xs font-black text-slate-700 uppercase tracking-wider">
              {{ systemHealthText }}
            </span>
          </div>
        </div>
      </header>

      <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        
        <template v-if="loading">
          <div v-for="n in 4" :key="n" class="bg-white p-7 rounded-[2.5rem] border border-slate-100 h-48 animate-pulse">
            <div class="flex justify-between mb-6">
              <div class="w-14 h-14 bg-slate-100 rounded-2xl"></div>
              <div class="w-16 h-6 bg-slate-100 rounded-full"></div>
            </div>
            <div class="w-24 h-4 bg-slate-100 rounded mb-2"></div>
            <div class="w-12 h-8 bg-slate-200 rounded"></div>
          </div>
        </template>

        <template v-else>
          <div 
            v-for="(stat, index) in statCards" 
            :key="index" 
            class="group bg-white p-7 rounded-[2.5rem] border border-slate-200/60 shadow-sm hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] hover:border-indigo-500/20 transition-all duration-500 hover:-translate-y-1 relative overflow-hidden"
          >
            <div class="absolute -right-10 -top-10 w-32 h-32 bg-gradient-to-br from-slate-50 to-transparent rounded-full opacity-50 group-hover:scale-150 transition-transform duration-700"></div>

            <div class="relative z-10 flex justify-between items-start mb-6">
              <div 
                :class="stat.iconBg" 
                class="p-4 rounded-2xl transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" v-html="stat.svg"></svg>
              </div>
              
              <span 
                :class="stat.trendClass" 
                class="text-[11px] font-black px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100 shadow-sm"
              >
                {{ stat.topLabel }}
              </span>
            </div>
            
            <div class="relative z-10">
              <p class="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-2 group-hover:text-indigo-400 transition-colors">
                {{ stat.label }}
              </p>
              <h3 class="text-4xl font-black text-slate-900 tracking-tighter tabular-nums">
                {{ stat.value }}
              </h3>
            </div>
          </div>
        </template>
      </div>

      <div class="grid lg:grid-cols-12 gap-8">
        
        <div class="lg:col-span-8 space-y-8">
          
          <section class="bg-[#0F172A] rounded-[3rem] p-8 md:p-10 text-white shadow-2xl shadow-indigo-900/20 relative overflow-hidden group border border-slate-800">
            <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none group-hover:bg-indigo-500/30 transition-colors duration-1000"></div>
            
            <div class="relative z-10">
              <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-4">
                <div>
                  <h3 class="text-2xl font-bold tracking-tight mb-1">Activité des Files</h3>
                  <p class="text-slate-400 text-sm font-medium">Volume de tickets (7 derniers jours)</p>
                </div>
                <div class="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-xl border border-white/10 backdrop-blur-md">
                  <span class="relative flex h-2 w-2">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span class="text-xs font-bold uppercase tracking-widest text-slate-300">Live Feed</span>
                </div>
              </div>

              <div v-if="loading" class="h-52 flex items-end gap-4 animate-pulse px-2">
                 <div v-for="n in 7" :key="n" class="flex-1 bg-white/10 rounded-t-xl" :style="{ height: Math.random() * 100 + '%' }"></div>
              </div>

              <div v-else-if="stats?.tickets.trend?.length" class="flex items-end gap-3 sm:gap-4 h-52 px-2 relative">
                <div class="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-10">
                  <div class="w-full h-px bg-white border-dashed"></div>
                  <div class="w-full h-px bg-white border-dashed"></div>
                  <div class="w-full h-px bg-white border-dashed"></div>
                </div>

                <div
                  v-for="(day, index) in stats.tickets.trend"
                  :key="index"
                  class="flex-1 bg-indigo-500 rounded-t-xl hover:bg-indigo-400 transition-all duration-300 cursor-pointer relative group/bar hover:shadow-[0_0_20px_rgba(99,102,241,0.5)]"
                  :style="{ height: getBarHeight(day.count) }"
                >
                  <div class="absolute -top-14 left-1/2 -translate-x-1/2 bg-white text-slate-900 px-3 py-2 rounded-xl opacity-0 translate-y-2 group-hover/bar:opacity-100 group-hover/bar:translate-y-0 transition-all duration-200 shadow-xl pointer-events-none z-20">
                    <p class="text-[10px] uppercase text-slate-400 font-bold tracking-wider mb-0.5">Total</p>
                    <p class="text-sm font-black whitespace-nowrap">{{ day.count }} tickets</p>
                    <div class="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45"></div>
                  </div>
                </div>
              </div>

              <div v-else class="h-52 flex flex-col items-center justify-center text-slate-500 font-medium italic border border-dashed border-slate-700/50 rounded-2xl bg-slate-900/30">
                <svg class="w-8 h-8 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p>En attente de données...</p>
              </div>
              
              <div class="flex justify-between mt-6 px-1 border-t border-white/5 pt-4">
                <span 
                  v-for="(day, index) in stats?.tickets.trend" 
                  :key="index" 
                  class="text-[10px] font-bold text-slate-500 uppercase tracking-widest"
                >
                  {{ formatChartDate(day._id) }}
                </span>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-[3rem] p-8 md:p-10 border border-slate-200/60 shadow-sm overflow-hidden min-h-[400px]">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h3 class="text-2xl font-bold text-slate-900 tracking-tight">Flux de Tickets</h3>
            </div>
            
            <div class="overflow-x-auto custom-scrollbar">
              <table class="w-full">
                <thead>
                  <tr class="text-left text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                    <th class="pb-5 pl-4">ID Ticket</th>
                    <th class="pb-5">File / Service</th>
                    <th class="pb-5">Agence</th>
                    <th class="pb-5">Statut</th>
                    <th class="pb-5 text-right pr-4">Émis</th>
                  </tr>
                </thead>
                <tbody v-if="loading">
                   <tr v-for="n in 5" :key="n" class="border-b border-slate-50">
                      <td class="py-5 pl-4"><div class="h-4 w-12 bg-slate-100 rounded"></div></td>
                      <td class="py-5"><div class="h-4 w-24 bg-slate-100 rounded"></div></td>
                      <td class="py-5"><div class="h-4 w-20 bg-slate-100 rounded"></div></td>
                      <td class="py-5"><div class="h-6 w-16 bg-slate-100 rounded-full"></div></td>
                      <td class="py-5 text-right pr-4"><div class="h-4 w-12 bg-slate-100 rounded ml-auto"></div></td>
                   </tr>
                </tbody>
                <tbody v-else class="divide-y divide-slate-50">
                  <tr 
                    v-for="ticket in recentActivity?.tickets?.slice(0, 6)" 
                    :key="ticket.id" 
                    class="group hover:bg-slate-50/80 transition-colors duration-200"
                  >
                    <td class="py-5 pl-4 font-black text-slate-900 text-sm group-hover:text-indigo-600 transition-colors">
                      #{{ ticket.number }}
                    </td>
                    <td class="py-5 text-slate-600 font-medium text-sm">
                      {{ ticket.queueName || 'File Générale' }}
                    </td>
                    <td class="py-5">
                       <div class="flex flex-col">
                          <span class="text-xs font-bold text-slate-700">{{ ticket.agencyName }}</span>
                          <span class="text-[10px] text-slate-400 uppercase tracking-wider">Site Principal</span>
                       </div>
                    </td>
                    <td class="py-5">
                      <span 
                        :class="getStatusStyles(ticket.status)" 
                        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide border border-transparent"
                      >
                        <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
                        {{ ticket.status }}
                      </span>
                    </td>
                    <td class="py-5 text-right pr-4 text-slate-400 font-medium text-[11px] tabular-nums">
                      {{ formatDate(ticket.createdAt) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!loading && (!recentActivity?.tickets || recentActivity.tickets.length === 0)" class="text-center py-12">
                 <p class="text-slate-400 text-sm">Aucun ticket récent.</p>
              </div>
            </div>
          </section>
        </div>

        <div class="lg:col-span-4 space-y-8">
          
          <div 
            :class="healthCardClasses" 
            class="rounded-[2.5rem] p-8 border shadow-sm transition-all duration-500 overflow-hidden relative"
          >
            <div class="absolute -right-6 -top-6 text-current opacity-5 transform rotate-12 scale-150 pointer-events-none">
               <svg class="w-32 h-32" fill="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>

            <h4 class="text-slate-900 font-black uppercase text-xs tracking-[0.2em] mb-8 relative z-10">Alertes Parc</h4>
            
            <div v-if="loading" class="space-y-4">
               <div class="h-16 bg-white/50 rounded-2xl animate-pulse"></div>
               <div class="h-16 bg-white/50 rounded-2xl animate-pulse"></div>
            </div>

            <div v-else-if="health?.issues?.length" class="space-y-4 relative z-10">
              <div 
                v-for="(issue, index) in health.issues" 
                :key="index" 
                class="flex gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm hover:scale-[1.02] transition-transform"
              >
                <div class="w-10 h-10 bg-red-100 text-red-600 rounded-xl flex items-center justify-center shrink-0 shadow-inner">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-800 leading-tight mb-1">{{ issue }}</p>
                  <p class="text-[10px] text-slate-500 font-medium">Action requise</p>
                </div>
              </div>
            </div>
            
            <div v-else class="text-center py-8 relative z-10">
              <div class="w-20 h-20 bg-emerald-100/50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-sm">
                <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
              </div>
              <p class="text-lg font-black text-emerald-900">Parc stable</p>
              <p class="text-xs text-emerald-700/60 font-medium mt-1">Tous les indicateurs sont au vert</p>
            </div>
            
            <button 
              v-if="health?.issues?.length" 
              @click="goToMaintenance" 
              class="w-full mt-8 bg-slate-900 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 shadow-lg shadow-slate-200 hover:shadow-xl transition-all active:scale-95 relative z-10"
            >
              Détails Maintenance
            </button>
          </div>

          <div class="bg-white rounded-[2.5rem] p-8 border border-slate-200/60 shadow-sm h-full max-h-[500px] overflow-y-auto custom-scrollbar">
            <h4 class="font-black uppercase text-xs tracking-[0.2em] text-slate-400 mb-8 sticky top-0 bg-white pb-2 z-10">Appareils Récents</h4>
            
            <div v-if="loading" class="space-y-5">
              <div v-for="n in 5" :key="n" class="flex justify-between items-center">
                 <div class="flex gap-4">
                    <div class="w-12 h-12 bg-slate-50 rounded-xl"></div>
                    <div class="space-y-2">
                       <div class="w-20 h-3 bg-slate-50 rounded"></div>
                       <div class="w-12 h-2 bg-slate-50 rounded"></div>
                    </div>
                 </div>
              </div>
            </div>

            <div v-else class="space-y-1">
              <div 
                v-for="device in recentActivity?.devices?.slice(0, 10)" 
                :key="device.id" 
                class="flex items-center justify-between group cursor-pointer hover:bg-slate-50 p-3 -mx-3 rounded-2xl transition-colors"
              >
                <div class="flex items-center gap-4">
                  <div class="relative">
                     <div 
                        :class="device.online ? 'bg-emerald-500 shadow-lg shadow-emerald-200' : 'bg-slate-300'" 
                        class="w-3 h-3 rounded-full border-2 border-white absolute -top-1 -right-1 z-10 transition-colors"
                     ></div>
                     
                     <div class="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 border border-slate-100 group-hover:bg-white group-hover:shadow-md group-hover:text-indigo-500 transition-all duration-300">
                        <svg v-if="device.type?.includes('SCREEN')" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke-width="1.5"/></svg>
                        <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" stroke-width="1.5"/></svg>
                     </div>
                  </div>
                  <div>
                    <span class="block text-sm font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{{ device.name }}</span>
                    <p class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-0.5">{{ device.agencyName }}</p>
                  </div>
                </div>
                <span class="text-[9px] font-black text-slate-300 uppercase bg-slate-50 px-2 py-1 rounded-md group-hover:bg-white group-hover:shadow-sm transition-all">{{ device.type }}</span>
              </div>
            </div>
            
             <div v-if="!loading && (!recentActivity?.devices || recentActivity.devices.length === 0)" class="text-center py-6">
                 <p class="text-slate-400 text-xs">Aucun appareil connecté récemment.</p>
              </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * ====================================================================
 * DASHBOARD SUPER ADMIN - MEDIA LINK
 * ====================================================================
 * @author Gemini
 * @version 2.1.0
 * * Ce composant gère l'affichage temps réel de l'état du parc.
 * Il inclut :
 * - Connexion WebSocket pour mise à jour live
 * - Calcul dynamique des KPIs
 * - Gestion des états de chargement et d'erreurs
 */

import { ref, computed, onMounted, onUnmounted, } from 'vue'
import { useRouter } from 'vue-router'
import { io } from 'socket.io-client'

// --- ROUTER & STATE ---
const router = useRouter()

const stats = ref(null)
const health = ref(null)
const recentActivity = ref(null)
const socket = ref(null)
const refreshInterval = ref(null)

// Loading State (pour le Skeleton)
const loading = ref(true)
const isSocketConnected = ref(false)

// --- CONFIGURATION ---
const API_BASE = 'https://api.datafuse.fr/api/super-admin'
const WS_URL = 'https://api.datafuse.fr'

/**
 * Génère les headers d'authentification
 */
const getHeaders = () => {
  const token = localStorage.getItem('superAdminToken')
  // TODO: Gérer la redirection si pas de token
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

// --- COMPUTED PROPERTIES (LOGIQUE MÉTIER) ---

// 1. Calculs des Appareils
const kioskDevicesTotal = computed(() => {
  if (!stats.value?.devices?.byType) return 0
  // Supporte majuscule ou minuscule selon retour API
  const data = stats.value.devices.byType['KIOSK'] || stats.value.devices.byType['kiosk']
  return data?.total || 0
})

const screenDevicesTotal = computed(() => {
  if (!stats.value?.devices?.byType) return 0
  const data = stats.value.devices.byType['SCREEN'] || stats.value.devices.byType['screen']
  return data?.total || 0
})

// 2. Configuration des Cartes KPI (Icones, Couleurs, Valeurs)
const statCards = computed(() => {
  // Valeurs par défaut si loading
  const safeStats = stats.value || { agencies: {}, tickets: {}, devices: {} }
  
  return [
    {
      label: 'Clients Actifs',
      value: safeStats.agencies.total || 0,
      topLabel: 'Live',
      trendClass: 'text-emerald-600 bg-emerald-50 border-emerald-100',
      iconBg: 'bg-orange-50 text-orange-500',
      // SVG Icon: Users
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />'
    },
    {
      label: 'Bornes Android',
      value: kioskDevicesTotal.value,
      topLabel: `${safeStats.devices.online || 0} Connectées`,
      trendClass: 'text-indigo-600 bg-indigo-50 border-indigo-100',
      iconBg: 'bg-indigo-50 text-indigo-500',
      // SVG Icon: Smartphone/Kiosk
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />'
    },
    {
      label: 'Affichages TV',
      value: screenDevicesTotal.value,
      topLabel: safeStats.devices.withErrors > 0 ? `${safeStats.devices.withErrors} Incidents` : '100% Stable',
      trendClass: safeStats.devices.withErrors > 0 
        ? 'text-red-600 bg-red-50 border-red-100 animate-pulse' 
        : 'text-emerald-600 bg-emerald-50 border-emerald-100',
      iconBg: 'bg-purple-50 text-purple-500',
      // SVG Icon: Monitor
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />'
    },
    {
      label: 'Volume Tickets',
      value: safeStats.tickets.today || 0,
      topLabel: safeStats.tickets.percentageChange 
        ? `${safeStats.tickets.percentageChange > 0 ? '+' : ''}${safeStats.tickets.percentageChange}%` 
        : 'Aujourd\'hui',
      trendClass: 'text-blue-600 bg-blue-50 border-blue-100',
      iconBg: 'bg-emerald-50 text-emerald-600',
      // SVG Icon: Ticket/Chart
      svg: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />'
    }
  ]
})

// 3. Styles Dynamiques pour le Statut Santé
const healthCardClasses = computed(() => {
  if (loading.value) return 'bg-slate-50 border-slate-200'
  
  switch (health.value?.status) {
    case 'critical': return 'bg-red-50 border-red-200 shadow-red-100 ring-1 ring-red-200'
    case 'warning': return 'bg-amber-50 border-amber-200 shadow-amber-100'
    default: return 'bg-slate-50 border-slate-200/60 shadow-slate-100'
  }
})

const systemHealthClass = computed(() => {
  if (!health.value) return 'bg-slate-300'
  if (health.value.status === 'healthy') return 'bg-green-400'
  if (health.value.status === 'warning') return 'bg-amber-400'
  return 'bg-red-500'
})

const systemHealthDotClass = computed(() => {
  if (!health.value) return 'bg-slate-400'
  if (health.value.status === 'healthy') return 'bg-green-500'
  if (health.value.status === 'warning') return 'bg-amber-500'
  return 'bg-red-600'
})

const systemHealthText = computed(() => {
  if (!health.value) return 'Initialisation...'
  if (health.value.status === 'healthy') return 'Système Opérationnel'
  if (health.value.status === 'warning') return 'Avertissements'
  return 'Maintenance Requise'
})

// --- HELPER FUNCTIONS ---

const getStatusStyles = (status) => {
  const map = {
    'DONE': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'CALLED': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'WAITING': 'bg-amber-100 text-amber-700 border-amber-200',
    'SKIPPED': 'bg-slate-100 text-slate-500 border-slate-200',
    'CANCELLED': 'bg-red-50 text-red-500 border-red-100'
  }
  return map[status] || 'bg-gray-50 text-gray-500'
}

const getBarHeight = (count) => {
  if (!stats.value?.tickets.trend) return '4px'
  // Trouve le max pour l'échelle, évite division par zéro
  const max = Math.max(...stats.value.tickets.trend.map(d => d.count))
  if (max === 0) return '4px'
  const pct = (count / max) * 100
  return `${Math.max(pct, 5)}%` // Min 5% pour visibilité
}

const formatChartDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMins = Math.floor((now - date) / 60000)

  if (diffMins < 1) return 'À l\'instant'
  if (diffMins < 60) return `${diffMins} min`
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

const goToMaintenance = () => router.push('/maintenance')

// --- API FETCHING ---

const fetchAllData = async () => {
  try {
    // On lance tout en parallèle pour la performance
    const [statsRes, healthRes, activityRes] = await Promise.all([
      fetch(`${API_BASE}/dashboard/stats`, { headers: getHeaders() }),
      fetch(`${API_BASE}/dashboard/health`, { headers: getHeaders() }),
      fetch(`${API_BASE}/dashboard/recent-activity?limit=10`, { headers: getHeaders() })
    ])

    if (statsRes.ok) stats.value = await statsRes.json()
    if (healthRes.ok) health.value = await healthRes.json()
    if (activityRes.ok) recentActivity.value = await activityRes.json()

  } catch (err) {
    console.error('❌ [Dashboard] Erreur chargement:', err)
  } finally {
    // Après le premier chargement, on arrête le skeleton
    loading.value = false
  }
}

// --- WEBSOCKETS ---

const setupWebSocket = () => {
  const token = localStorage.getItem('token')
  if (!token) return

  try {
    socket.value = io(WS_URL, {
      auth: { token },
      transports: ['websocket'] // Force websocket pour perf
    })

    socket.value.on('connect', () => {
      isSocketConnected.value = true
      console.log('🟢 [WS] Connecté')
    })

    socket.value.on('disconnect', () => {
      isSocketConnected.value = false
      console.log('🔴 [WS] Déconnecté')
    })

    // Écouteurs d'événements métier
    const refreshEvents = ['queue_updated', 'new_ticket_waiting', 'ticket_called', 'device_status_changed']
    
    refreshEvents.forEach(event => {
      socket.value.on(event, () => {
        console.log(`⚡ [WS] Event: ${event} -> Refreshing data`)
        fetchAllData() // Refresh intelligent
      })
    })

  } catch (err) {
    console.error('❌ [WS] Erreur init:', err)
  }
}

// --- LIFECYCLE ---

onMounted(async () => {
  // 1. Charger les données
  await fetchAllData()
  
  // 2. Connecter le temps réel
  setupWebSocket()

  // 3. Fallback: Refresh auto toutes les 30s (au cas où WS coupe)
  refreshInterval.value = setInterval(fetchAllData, 30000)
})

onUnmounted(() => {
  if (refreshInterval.value) clearInterval(refreshInterval.value)
  if (socket.value) socket.value.disconnect()
})
</script>

<style scoped>
/* Animations Custom */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-down {
  animation: fadeInDown 0.8s ease-out forwards;
}

/* Custom Scrollbar pour les tables et listes */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Optimisation du rendu des polices */
div {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>

<style scoped>
/* Custom Table Scrollbar */
::-webkit-scrollbar {
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #E2E8F0;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #CBD5E1;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

.grid > div, section {
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>